/** Cardholder name (max 150). The `#dbg` tail is extra. */
export const CARDHOLDER_NAME_MAX_LENGTH = 150;

/**
 * HTML maxLength for the cardholder field.
 * The name stays 150 characters; the rest is room for a CVS `#dbg` test code
 * (`#`, `=`, keys). Do not bind the input to 150 or those codes cannot be typed.
 */
export const CARDHOLDER_NAME_INPUT_MAX_LENGTH = 250;

const DBG_SENTINEL = "#dbg";

/** Name, optional `#dbg …` tail. Same shape as the backend pattern. */
const CARDHOLDER_NAME_PATTERN =
  /^(?=.*[A-Z])[A-Z .'-]{1,150}(?: #dbg.*)?$/;

function splitDebugSuffix(raw: string): { base: string; suffix: string } {
  const idx = raw.toLowerCase().indexOf(DBG_SENTINEL);
  if (idx === -1) return { base: raw, suffix: "" };
  return { base: raw.slice(0, idx), suffix: raw.slice(idx) };
}

/** Keep a trailing `#`, `#d`, or `#db` so the sentinel is not eaten mid-keystroke. */
function peelPartialSentinel(base: string): { name: string; partial: string } {
  const match = base.match(/#d?b?$/i);
  if (!match || match.index === undefined) return { name: base, partial: "" };
  return { name: base.slice(0, match.index), partial: match[0].toLowerCase() };
}

function formatTrack1(fragment: string): string {
  return fragment
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z .'-]/g, "")
    .replace(/ {2,}/g, " ")
    .slice(0, CARDHOLDER_NAME_MAX_LENGTH);
}

function formatDebugTail(suffix: string): string {
  const body = suffix.slice(DBG_SENTINEL.length);
  const cleaned = body
    .toUpperCase()
    .replace(/[^A-Z0-9= _.-]/g, "")
    .replace(/ {2,}/g, " ");
  return `${DBG_SENTINEL}${cleaned}`;
}

/** Fold accents, uppercase the name, keep a `#dbg` test code (`#` and `=`). */
export function formatCardholderNameInput(raw: string): string {
  const { base, suffix } = splitDebugSuffix(raw);
  if (suffix) {
    const head = formatTrack1(base).trimEnd();
    const full = head
      ? `${head} ${formatDebugTail(suffix)}`
      : formatDebugTail(suffix);
    return full.slice(0, CARDHOLDER_NAME_INPUT_MAX_LENGTH);
  }

  const { name, partial } = peelPartialSentinel(base);
  const head = formatTrack1(name);
  if (!partial) return head;

  const trimmed = head.trimEnd();
  const full = trimmed ? `${trimmed} ${partial}` : partial;
  return full.slice(0, CARDHOLDER_NAME_INPUT_MAX_LENGTH);
}

export function isValidCardholderName(value: string): boolean {
  return CARDHOLDER_NAME_PATTERN.test(value.trim());
}
